import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
	Calendar,
	Clock,
	Share2,
	Facebook,
	Twitter,
	Linkedin,
	ArrowLeft,
	ArrowRight,
} from "lucide-react";
import posts from "../../blog-posts";
import MarkdownPost from "../../components/MarkdownPost";

const RelatedPost = ({ title, excerpt, image, id }) => (
	<Link
		to={`/blog/${id}`}
		className="group block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:border-primary/20 transition-colors"
	>
		<div className="aspect-w-16 aspect-h-9 overflow-hidden">
			<img
				src={image}
				alt={title}
				className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
			/>
		</div>
		<div className="p-4">
			<h3 className="font-medium text-gray-900 group-hover:text-primary transition-colors mb-2">
				{title}
			</h3>
			<p className="text-sm text-gray-600 line-clamp-2">{excerpt}</p>
		</div>
	</Link>
);

export default function BlogPost() {
	const { id } = useParams();
	const post = posts.find((post) => post.id.toString() === id);

	// Find previous and next posts
	const currentIndex = posts.findIndex((p) => p.id.toString() === id);
	const prevPost = currentIndex > 0 ? posts[currentIndex - 1] : null;
	const nextPost =
		currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null;

	// Get related posts (excluding current post)
	const relatedPosts = posts.filter((p) => p.id.toString() !== id).slice(0, 3);

	if (!post) {
		return (
			<div className="min-h-screen bg-gray-50 py-24">
				<div className="max-w-xl mx-auto px-4 text-center">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Post Not Found
					</h2>
					<p className="text-gray-600 mb-8">
						The blog post you're looking for doesn't exist or has been removed.
					</p>
					<Link
						to="/blog"
						className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
					>
						<ArrowLeft className="w-5 h-5 mr-2" />
						Back to Blog
					</Link>
				</div>
			</div>
		);
	}

	return (
		<>
			<Helmet>
				<title>Blog | {post.title} | Off Cut Shades</title>
				<meta name="description" content={post.content} />
				<script type="application/ld+json">
					{`{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://offcutshades.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://offcutshades.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "${post.title}",
                "item": "https://offcutshades.com/blog/${id}"
              }
            ]
          }`}
				</script>
			</Helmet>

			<main className="min-h-screen bg-gray-50">
				{/* Hero Section */}
				<div className="relative bg-white border-b border-gray-200">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
						<Link
							to="/blog"
							className="inline-flex items-center text-gray-600 hover:text-primary mb-8 transition-colors"
						>
							<ArrowLeft className="w-4 h-4 mr-2" />
							Back to Blog
						</Link>

						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							className="max-w-3xl"
						>
							<h1 className="text-4xl font-bold text-gray-900 mb-4">
								{post.title}
							</h1>

							<div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8">
								<div className="flex items-center gap-1">
									<Calendar className="w-4 h-4" />
									<time dateTime={post.date}>
										{new Date(post.date).toLocaleDateString("en-US", {
											year: "numeric",
											month: "long",
											day: "numeric",
										})}
									</time>
								</div>
								<div className="flex items-center gap-1">
									<Clock className="w-4 h-4" />
									<span>5 min read</span>
								</div>
							</div>
						</motion.div>
					</div>
				</div>

				{/* Content Section */}
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
						{/* Main Content */}
						<div className="lg:col-span-2">
							<article className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 prose prose-lg prose-gray max-w-none">
								<MarkdownPost {...post} />
							</article>

							{/* Post Navigation */}
							<div className="mt-8 grid grid-cols-2 gap-4">
								{prevPost && (
									<Link
										to={`/blog/${prevPost.id}`}
										className="group p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary/20 transition-colors"
									>
										<div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
											<ArrowLeft className="w-4 h-4" />
											Previous Post
										</div>
										<h3 className="font-medium text-gray-900 group-hover:text-primary transition-colors">
											{prevPost.title}
										</h3>
									</Link>
								)}
								{nextPost && (
									<Link
										to={`/blog/${nextPost.id}`}
										className="group p-4 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary/20 transition-colors text-right"
									>
										<div className="flex items-center justify-end gap-2 text-gray-500 text-sm mb-2">
											Next Post
											<ArrowRight className="w-4 h-4" />
										</div>
										<h3 className="font-medium text-gray-900 group-hover:text-primary transition-colors">
											{nextPost.title}
										</h3>
									</Link>
								)}
							</div>
						</div>

						{/* Sidebar */}
						<div className="space-y-8">
							{/* Related Posts */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium text-gray-900 mb-4">
									Related Posts
								</h3>
								<div className="space-y-4">
									{relatedPosts.map((post) => (
										<RelatedPost key={post.id} {...post} />
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
		</>
	);
}
